import { useEffect, useRef, useState } from 'react';
import { SORTES_CLIENT } from '../compta/ClasseurClients.jsx';
import { DocumentsJoints } from '../../components/DocumentsJoints.jsx';
import { ListeDuClient } from '../../components/ListeDuClient.jsx';
import { ttcDocument } from '../../lib/vat';
import { clientsApi } from '../../api/resources';
import { clientVersDto, dtoVersClient } from '../../api/mappers';
import { Shell } from '../../components/layout/Shell.jsx';
import { SignPad } from '../../components/SignPad.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { FR } from '../../components/ui/FieldRow.jsx';
import { Inp, inpFS } from '../../components/ui/Inputs.jsx';
import { Pill } from '../../components/ui/Pill.jsx';
import { SOC } from '../../constants/company';
import { idProvisoire } from '../../lib/identifiants';
import { BoutonContacts } from '../../components/BoutonContacts.jsx';
import { clientExistant } from '../../lib/contacts';
import { DonneesPersonnellesFiche } from '../../components/DonneesPersonnellesFiche.jsx';
import { ACCESS_ROLE, voitLeBenefice } from '../../lib/access';
import { DEVISE, fmt } from '../../lib/currency';
import { ChoixDelaiPaiement } from './ChoixDelaiPaiement.jsx';
import { HistoriqueEchanges } from './HistoriqueEchanges.jsx';
import { PersonnesAContacter } from './PersonnesAContacter.jsx';
import { EnvoiCatalogue } from '../stock/EnvoiCatalogue.jsx';
import { tr, trf, trU } from '../../lib/i18n';
import { avecExigence, exigeBonCommande } from '../../lib/bon-commande';
import { printDoc } from '../../lib/print';
import { numeroDe, ordreDeSaisie, today } from '../../lib/utils';
import { C } from '../../theme/palette';
import { termesOffre } from '../../lib/terminologie';

export function ClientsPage({
  onNav,
  soc,
  clients,
  setClients,
  devis,
  factures,
  chantiers,
  catalogue,
  stock,
  estMixte,
  univers,
  setUnivers,
  startOuv,
  ouvrir,
}) {
  const [selId, setSelId] = useState(null);
  // La recherche mène à la fiche du client touché, et non à la liste. Une
  // seule fois : la liste rechargée ne doit pas la rouvrir après « ← ».
  const dejaOuvert = useRef(null);
  useEffect(() => {
    if (ouvrir?.clientId == null || dejaOuvert.current === ouvrir) return;
    if (!(clients || []).some((c) => c.id === ouvrir.clientId)) return;
    dejaOuvert.current = ouvrir;
    setSelId(ouvrir.clientId);
  }, [ouvrir, clients]);
  const [q, setQ] = useState('');
  const [edit, setEdit] = useState(false);
  const [plusFiche, setPlusFiche] = useState(false);
  const [form, setForm] = useState(null);
  const [add, setAdd] = useState(false);
  // Un client lié à des pièces se supprime aussi : ses offres, factures et
  // chantiers restent, sans client. L'écran refusait tout court (« documents
  // liés »), sans autre issue.
  const supprimeClient = (cl) => {
    const lies =
      (devis || []).filter((d) => d.clientId === cl.id).length +
      (factures || []).filter((f) => f.clientId === cl.id).length +
      (chantiers || []).filter((c) => c.clientId === cl.id).length;
    const question = lies
      ? trf(
          'Supprimer {0} ?\n\nSes {1} offre(s), facture(s) et chantier(s) restent, sans client.',
          cl.nom,
          lies,
        )
      : trf('Supprimer {0} ?', cl.nom);
    if (!window.confirm(question)) return;
    setClients((p) => p.filter((c) => c.id !== cl.id));
    setSelId(null);
  };
  const [cliMsg, setCliMsg] = useState('');
  const [remArtQ, setRemArtQ] = useState('');
  // « Envoyer le catalogue à ce client » depuis sa fiche : le prix public et
  // ses prix, à ses personnes. Il n'était que dans Stock → % Remises.
  const [envoiCatalogue, setEnvoiCatalogue] = useState(false);
  const [ouvForm, setOuvForm] = useState(
    startOuv
      ? {
          nom: '',
          forme: '',
          rue: '',
          casePostale: '',
          npa: '',
          profession: '',
          tva: '',
          nbEmployes: '',
          contactNom: '',
          fonction: '',
          tel: '',
          mobile: '',
          email: '',
          emailFactures: '',
          accepteElec: false,
          emailCorr: '',
          telFacturation: '',
          manuel: true,
          dateDemande: today(),
        }
      : null,
  );
  const [ouvSent, setOuvSent] = useState(false);
  const [ouvNewId, setOuvNewId] = useState(null);
  const [ficheA4, setFicheA4] = useState(null);
  const estMagC = estMixte ? univers === 'magasin' : soc?.secteur === 'magasin';
  // Le fichier lui-meme est garde en plus de son apercu : il part en piece
  // jointe vers le stockage, et non encode dans la fiche — un extrait PDF y
  // gonflerait la ligne du client d'un tiers de son poids, pour rien.
  const readDoc = (file, key) => {
    if (!file) return;
    const r = new FileReader();
    r.onload = () =>
      setOuvForm((p) => ({
        ...(p || {}),
        [key]: r.result,
        [key + 'Nom']: file.name,
        [key + 'Fichier']: file,
      }));
    r.readAsDataURL(file);
  };
  // Contact du carnet ou fiche .vcf : le formulaire se remplit de la même
  // façon, et reste à vérifier avant la création.
  const recoitContact = (contact) => {
    setForm(contact);
    const deja = clientExistant(clients, contact);
    setCliMsg(
      deja
        ? '⚠️ « ' +
            deja.nom +
            ' » est déjà dans tes clients : pas besoin de le créer une seconde fois.'
        : contact.nom
          ? '✓ « ' + contact.nom + ' » importé — vérifie puis crée.'
          : 'Contact importé — complète les champs ci-dessous.',
    );
  };
  // Ce que le client a reellement recu comme facture, remise et taux par ligne
  // compris.
  const ttcF = ttcDocument;

  if (ouvForm) {
    const f = ouvForm;
    return (
      <Shell
        active="plus"
        onNav={onNav}
        soc={soc}
        back
        onBack={() => {
          setOuvForm(null);
          setOuvSent(false);
        }}
        pageTitle="📋 Ouverture de compte"
        estMixte={estMixte}
        univers={univers}
        setUnivers={setUnivers}
      >
        {ouvSent ? (
          <Card style={{ border: '2px solid ' + C.green }}>
            <div style={{ padding: 18, textAlign: 'center' }}>
              <div style={{ fontSize: 34, marginBottom: 8 }}>✅</div>
              <div style={{ fontSize: 14, fontWeight: 900, color: C.ink, marginBottom: 6 }}>
                {f.manuel ? 'Compte client ouvert' : 'Demande envoyée'}
              </div>
              {f.manuel ? (
                <>
                  <div style={{ fontSize: 11, color: C.grey, lineHeight: 1.5 }}>
                    Le compte de <b>{f.nom}</b> est ouvert.
                  </div>
                  <div
                    style={{ fontSize: 13, fontWeight: 900, color: C.navy, margin: '8px 0 14px' }}
                  >
                    N° client : {ouvNewId}
                  </div>
                  <Btn
                    label="🖨 Imprimer la fiche A4"
                    color={C.navy}
                    full
                    onClick={() => {
                      setOuvForm(null);
                      setOuvSent(false);
                      setFicheA4(ouvNewId);
                    }}
                  />
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <Btn
                      label="✉ E-mail"
                      color={C.teal}
                      full
                      onClick={() => {
                        const to = f.emailFactures || f.email || '';
                        const msg =
                          'Bonjour,\n\nVotre compte client a bien été ouvert chez ' +
                          (soc?.nom || '') +
                          '.\nVotre numéro de client : ' +
                          ouvNewId +
                          '.\n\nCordialement,\n' +
                          (soc?.nom || '');
                        try {
                          window.location.href =
                            'mailto:' +
                            to +
                            '?subject=' +
                            encodeURIComponent('Ouverture de votre compte client') +
                            '&body=' +
                            encodeURIComponent(msg);
                        } catch (e) {}
                      }}
                    />
                    <Btn
                      label="🟢 WhatsApp"
                      color={C.green}
                      full
                      onClick={() => {
                        const msg =
                          'Bonjour, votre compte client a bien été ouvert chez ' +
                          (soc?.nom || '') +
                          '. Votre numéro de client : ' +
                          ouvNewId +
                          '.';
                        try {
                          window.open('https://wa.me/?text=' + encodeURIComponent(msg), '_blank');
                        } catch (e) {}
                      }}
                    />
                  </div>
                  <Btn
                    label="＋ Ouvrir un autre compte"
                    ghost
                    color={C.navy}
                    full
                    onClick={() => {
                      setOuvSent(false);
                      setOuvNewId(null);
                      setOuvForm({
                        nom: '',
                        forme: '',
                        rue: '',
                        casePostale: '',
                        npa: '',
                        profession: '',
                        tva: '',
                        nbEmployes: '',
                        contactNom: '',
                        fonction: '',
                        tel: '',
                        mobile: '',
                        email: '',
                        emailFactures: '',
                        accepteElec: false,
                        emailCorr: '',
                        telFacturation: '',
                        manuel: true,
                        dateDemande: today(),
                      });
                    }}
                    style={{ marginTop: 10 }}
                  />
                  <Btn
                    label="Fermer"
                    ghost
                    color={C.grey}
                    full
                    onClick={() => {
                      setOuvForm(null);
                      setOuvSent(false);
                      onNav && onNav('clients');
                    }}
                    style={{ marginTop: 6 }}
                  />
                </>
              ) : (
                <>
                  <div style={{ fontSize: 10.5, color: C.grey, lineHeight: 1.5 }}>
                    Votre demande d'ouverture de compte a bien été transmise. Vous serez recontacté
                    après validation.
                  </div>
                  <Btn
                    label="Fermer"
                    color={C.navy}
                    full
                    onClick={() => {
                      setOuvForm(null);
                      setOuvSent(false);
                    }}
                    style={{ marginTop: 14 }}
                  />
                </>
              )}
            </div>
          </Card>
        ) : (
          <Card>
            <div style={{ padding: 14 }}>
              <div style={{ fontSize: 12.5, fontWeight: 900, color: C.ink, marginBottom: 2 }}>
                Fiche d'ouverture de compte client
              </div>
              <div style={{ fontSize: 9, color: C.muted, marginBottom: 6, lineHeight: 1.5 }}>
                {f.manuel
                  ? 'Saisis toutes les coordonnées du client pour lui ouvrir un compte. Un numéro client permanent lui sera attribué.'
                  : 'Remplissez vos informations et joignez vos documents. Votre demande sera validée par ' +
                    (soc?.nom || "l'entreprise") +
                    '.'}
              </div>
              {f.manuel && (
                <Btn
                  label="📄 Imprimer une fiche vierge (A4)"
                  ghost
                  color={C.navy}
                  full
                  onClick={() => {
                    setOuvForm(null);
                    setOuvSent(false);
                    setFicheA4('vierge');
                  }}
                  style={{ marginBottom: 10 }}
                />
              )}

              <div
                style={{
                  fontSize: 9.5,
                  fontWeight: 800,
                  color: C.navy,
                  margin: '12px 0 6px',
                  borderBottom: '2px solid ' + C.navyXL,
                  paddingBottom: 3,
                }}
              >
                🏢 Informations de l'entreprise
              </div>
              {[
                ["Nom de l'entreprise *", 'nom'],
                ['Forme juridique (SA, Sàrl, individuelle…)', 'forme'],
                ['Rue / N° *', 'rue'],
                ['Case postale', 'casePostale'],
                ['NPA / Lieu *', 'npa'],
                ['Profession / Branche *', 'profession'],
                ['N° TVA', 'tva'],
                ["Nombre d'employés *", 'nbEmployes'],
              ].map(([lb, k]) => (
                <Inp
                  key={k}
                  label={lb}
                  value={f[k] || ''}
                  onChange={(e) => setOuvForm((p) => ({ ...(p || {}), [k]: e.target.value }))}
                />
              ))}

              <div
                style={{
                  fontSize: 9.5,
                  fontWeight: 800,
                  color: C.navy,
                  margin: '14px 0 6px',
                  borderBottom: '2px solid ' + C.navyXL,
                  paddingBottom: 3,
                }}
              >
                👤 Informations de contact
              </div>
              {[
                ['Nom du contact principal *', 'contactNom'],
                ['Fonction', 'fonction'],
                ['Téléphone *', 'tel'],
                ['Téléphone mobile *', 'mobile'],
                ['E-mail *', 'email'],
              ].map(([lb, k]) => (
                <Inp
                  key={k}
                  label={lb}
                  value={f[k] || ''}
                  onChange={(e) => setOuvForm((p) => ({ ...(p || {}), [k]: e.target.value }))}
                />
              ))}

              <div
                style={{
                  fontSize: 9.5,
                  fontWeight: 800,
                  color: C.navy,
                  margin: '14px 0 6px',
                  borderBottom: '2px solid ' + C.navyXL,
                  paddingBottom: 3,
                }}
              >
                🧾 Informations de facturation
              </div>
              <Inp
                label="E-mail pour envoi des factures *"
                value={f.emailFactures || ''}
                onChange={(e) =>
                  setOuvForm((p) => ({ ...(p || {}), emailFactures: e.target.value }))
                }
              />
              <div
                onClick={() => setOuvForm((p) => ({ ...(p || {}), accepteElec: !p.accepteElec }))}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  padding: '6px 0 8px',
                  cursor: 'pointer',
                }}
              >
                <div
                  style={{
                    width: 18,
                    height: 18,
                    minWidth: 18,
                    borderRadius: 5,
                    border: '2px solid ' + C.navy,
                    background: f.accepteElec ? C.navy : '#fff',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    fontWeight: 900,
                  }}
                >
                  {f.accepteElec ? '✓' : ''}
                </div>
                <span style={{ fontSize: 9, color: C.grey, lineHeight: 1.4 }}>
                  J'accepte de recevoir les factures par voie électronique au format PDF.
                </span>
              </div>
              {[
                ['E-mail correspondance (si différent)', 'emailCorr'],
                ['Téléphone de la facturation *', 'telFacturation'],
              ].map(([lb, k]) => (
                <Inp
                  key={k}
                  label={lb}
                  value={f[k] || ''}
                  onChange={(e) => setOuvForm((p) => ({ ...(p || {}), [k]: e.target.value }))}
                />
              ))}
              {/*
                Le delai negocie vaut pour toutes les factures de ce client :
                l'inscrire ici evite de le retaper a chaque piece, et evite
                surtout l'erreur qui fait relancer un client qui n'est pas en
                retard.
              */}
              <ChoixDelaiPaiement
                value={f.delaiPaiement ?? ''}
                onChange={(delai) => setOuvForm((p) => ({ ...(p || {}), delaiPaiement: delai }))}
              />

              <div
                style={{
                  fontSize: 9.5,
                  fontWeight: 800,
                  color: C.navy,
                  margin: '14px 0 6px',
                  borderBottom: '2px solid ' + C.navyXL,
                  paddingBottom: 3,
                }}
              >
                📎 Documents
              </div>
              {[
                ['Extrait du registre du commerce *', 'docRC'],
                ['Extrait de poursuites *', 'docPoursuite'],
              ].map(([lb, k]) => (
                <div key={k} style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: C.grey, marginBottom: 4 }}>
                    {lb}
                  </div>
                  <label style={{ display: 'block' }}>
                    <div
                      style={{
                        background: f[k] ? C.green : C.navy,
                        color: '#fff',
                        borderRadius: 9,
                        padding: '10px',
                        textAlign: 'center',
                        fontSize: 10,
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      {f[k]
                        ? '✓ ' + (f[k + 'Nom'] || 'Document joint')
                        : '📎 Joindre le document (PDF ou photo)'}
                    </div>
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      style={{ display: 'none' }}
                      onChange={(e) => readDoc(e.target.files && e.target.files[0], k)}
                    />
                  </label>
                </div>
              ))}

              <div
                style={{
                  fontSize: 9.5,
                  fontWeight: 800,
                  color: C.navy,
                  margin: '14px 0 6px',
                  borderBottom: '2px solid ' + C.navyXL,
                  paddingBottom: 3,
                }}
              >
                ✍ Date & signature du client
              </div>
              <Inp
                label="Date *"
                value={f.dateDemande || ''}
                onChange={(e) => setOuvForm((p) => ({ ...(p || {}), dateDemande: e.target.value }))}
              />
              <div style={{ fontSize: 9, fontWeight: 700, color: C.grey, margin: '4px 0 4px' }}>
                Signature du client *
              </div>
              <SignPad
                value={f.signature || ''}
                onChange={(v) => setOuvForm((p) => ({ ...(p || {}), signature: v }))}
              />

              <Btn
                label={f.manuel ? '✓ Ouvrir le compte client' : '✓ Envoyer ma demande'}
                color={C.green}
                full
                disabled={
                  !(f.nom || '').trim() ||
                  (!f.manuel && (!f.docRC || !f.docPoursuite || !f.signature))
                }
                onClick={async () => {
                  // La demande passe directement par l'API plutot que par la
                  // collection : les deux extraits se deposent en piece jointe,
                  // ce qui suppose de connaitre l'identifiant attribue.
                  const fiche = {
                    nom: f.nom,
                    forme: f.forme || '',
                    rue: f.rue || '',
                    casePostale: f.casePostale || '',
                    adr: f.rue || '',
                    npa: f.npa || '',
                    profession: f.profession || '',
                    tva: f.tva || '',
                    ide: f.tva || '',
                    nbEmployes: f.nbEmployes || '',
                    contact: f.contactNom || '',
                    contactNom: f.contactNom || '',
                    fonction: f.fonction || '',
                    tel: f.tel || '',
                    mobile: f.mobile || '',
                    email: f.email || '',
                    emailFactures: f.emailFactures || '',
                    accepteElec: !!f.accepteElec,
                    emailCorr: f.emailCorr || '',
                    telFacturation: f.telFacturation || '',
                    signature: f.signature || '',
                    dateDemande: f.dateDemande || today(),
                    compteStatut: f.manuel ? 'actif' : 'demande',
                    dateOuverture: f.manuel ? today() : '',
                    remiseGlobale: 0,
                    remises: {},
                  };
                  try {
                    let enregistre = dtoVersClient(await clientsApi.cree(clientVersDto(fiche)));
                    const pieces = [
                      ['REGISTRE_COMMERCE', f.docRCFichier, f.docRCNom],
                      ['POURSUITES', f.docPoursuiteFichier, f.docPoursuiteNom],
                    ];
                    for (const [piece, fichier, nom] of pieces) {
                      if (!fichier) continue;
                      enregistre = dtoVersClient(
                        await clientsApi.deposePiece(enregistre.id, piece, fichier, nom),
                      );
                    }
                    setClients((p) => [...p, enregistre]);
                    setOuvNewId(enregistre.id);
                    setOuvSent(true);
                  } catch (e) {
                    setCliMsg(
                      "La demande n'a pas pu être enregistrée : " +
                        (e?.message || 'réessaie dans un instant.'),
                    );
                  }
                }}
                style={{ marginTop: 12 }}
              />
              <div style={{ fontSize: 8, color: C.muted, marginTop: 6 }}>
                * Champs obligatoires · ** acceptation des factures par e-mail (PDF).
              </div>
            </div>
          </Card>
        )}
      </Shell>
    );
  }

  if (ficheA4) {
    const vierge = ficheA4 === 'vierge';
    const cl = vierge ? {} : clients.find((c) => c.id === ficheA4);
    if (!cl) {
      return null;
    }
    const cond = cl.cond || {};
    const lblRow = (lb, val) => (
      <div style={{ display: 'flex', borderBottom: '1px solid #e8eaed', padding: '4.5px 0' }}>
        <div style={{ width: '44%', color: '#555', fontWeight: 600, fontSize: 8.5 }}>{lb}</div>
        <div style={{ flex: 1, fontWeight: 700, color: '#111', fontSize: 9 }}>{val || '—'}</div>
      </div>
    );
    const blank = (lb) => (
      <div style={{ display: 'flex', padding: '6px 0', alignItems: 'flex-end' }}>
        <div style={{ width: '42%', color: '#555', fontSize: 8.5 }}>{lb}</div>
        <div style={{ flex: 1, borderBottom: '1px solid #999', height: 13 }} />
      </div>
    );
    const row = (lb, val) => (vierge ? blank(lb) : lblRow(lb, val));
    return (
      <div
        style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#5b5f66' }}
      >
        <style>
          {
            '@media print{ body *{visibility:hidden} .fch,.fch *{visibility:visible} .fch{position:absolute;left:0;top:0;width:100%} .fchnp{display:none!important} }'
          }
        </style>
        <div
          className="fchnp"
          style={{
            background: C.navy,
            padding: '10px 14px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <button
            onClick={() => setFicheA4(null)}
            style={{
              background: 'none',
              border: 'none',
              color: '#fff',
              fontSize: 18,
              cursor: 'pointer',
            }}
          >
            ←
          </button>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#fff' }}>
            Fiche d'ouverture — A4
          </span>
          <button
            onClick={printDoc}
            style={{
              background: C.green,
              border: 'none',
              color: '#fff',
              fontSize: 11,
              fontWeight: 700,
              padding: '7px 12px',
              borderRadius: 8,
              cursor: 'pointer',
            }}
          >
            🖨 Imprimer
          </button>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: 14 }}>
          <div
            className="fch"
            style={{
              maxWidth: 680,
              margin: '0 auto',
              background: '#fff',
              borderRadius: 4,
              padding: '22px 22px',
              fontFamily: 'Arial, sans-serif',
              color: '#111',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                borderBottom: '2px solid ' + C.navy,
                paddingBottom: 10,
                marginBottom: 14,
              }}
            >
              <div>
                {soc?.logo && (
                  <img
                    src={soc.logo}
                    alt=""
                    style={{ height: 42, marginBottom: 6, objectFit: 'contain' }}
                  />
                )}
                <div style={{ fontSize: 13, fontWeight: 900 }}>{soc?.nom || SOC.nom}</div>
                <div style={{ fontSize: 8.5, color: '#555', lineHeight: 1.45 }}>
                  {soc?.adr || ''}
                  {soc?.npa ? ' · ' + soc.npa : ''}
                  <br />
                  {soc?.tel ? 'Tél. ' + soc.tel + '   ' : ''}
                  {soc?.email || ''}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 15, fontWeight: 900, color: C.navy }}>
                  Ouverture de compte
                </div>
                <div style={{ fontSize: 8.5, color: '#555' }}>
                  N° client {vierge ? '__________' : cl.id}
                </div>
                <div style={{ fontSize: 8.5, color: '#555' }}>
                  {vierge ? 'Date : __________' : cl.dateDemande || today()}
                </div>
              </div>
            </div>
            <div style={{ fontSize: 10, fontWeight: 900, color: C.navy, margin: '4px 0 4px' }}>
              Informations de l'entreprise
            </div>
            {row("Nom de l'entreprise", cl.nom)}
            {row('Forme juridique', cl.forme)}
            {row('Adresse (rue / n°)', cl.rue || cl.adr)}
            {row('Case postale', cl.casePostale)}
            {row('NPA / Lieu', cl.npa)}
            {row('Profession / Branche', cl.profession)}
            {row('N° TVA', cl.tva)}
            {row("Nombre d'employés", cl.nbEmployes)}
            <div style={{ fontSize: 10, fontWeight: 900, color: C.navy, margin: '12px 0 4px' }}>
              Informations de contact
            </div>
            {row('Contact principal', cl.contactNom || cl.contact)}
            {row('Fonction', cl.fonction)}
            {row('Téléphone', cl.tel)}
            {row('Téléphone mobile', cl.mobile)}
            {row('E-mail', cl.email)}
            <div style={{ fontSize: 10, fontWeight: 900, color: C.navy, margin: '12px 0 4px' }}>
              Informations de facturation
            </div>
            {row('E-mail factures', cl.emailFactures)}
            {row('Factures par e-mail (PDF)', vierge ? '' : cl.accepteElec ? 'Oui, accepté' : '—')}
            {row('E-mail correspondance', cl.emailCorr)}
            {row('Téléphone facturation', cl.telFacturation)}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                marginTop: 14,
                gap: 20,
              }}
            >
              <div style={{ fontSize: 8.5, color: '#555' }}>
                Date : <b>{vierge ? '__________' : cl.dateDemande || today()}</b>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 8, color: '#555', marginBottom: 2 }}>
                  Signature du client
                </div>
                {cl.signature ? (
                  <div style={{ width: 170 }}>
                    <SignPad value={cl.signature} onChange={() => {}} />
                  </div>
                ) : (
                  <div style={{ width: 170, borderBottom: '1px solid #999', height: 30 }} />
                )}
              </div>
            </div>
            <div
              style={{
                marginTop: 16,
                border: '1px solid ' + C.border,
                borderRadius: 6,
                padding: '10px 12px',
                background: '#f8fafc',
              }}
            >
              <div style={{ fontSize: 9.5, fontWeight: 900, color: C.navy, marginBottom: 4 }}>
                À remplir par {soc?.nom || SOC.nom}
              </div>
              {cond.representant || cond.condGroupe || cond.limiteCredit || cond.condPaiement ? (
                <div>
                  {lblRow('Représentant', cond.representant)}
                  {lblRow('Conditions groupe', cond.condGroupe)}
                  {lblRow('Limite de crédit', cond.limiteCredit)}
                  {lblRow('Conditions de paiement', cond.condPaiement)}
                </div>
              ) : (
                <div>
                  {blank('Représentant')}
                  {blank('Conditions groupe')}
                  {blank('Limite de crédit')}
                  {blank('Conditions de paiement')}
                </div>
              )}
              <div style={{ display: 'flex', gap: 20, marginTop: 4 }}>
                <div style={{ flex: 1 }}>{blank('Date')}</div>
                <div style={{ flex: 1 }}>{blank('Signature')}</div>
              </div>
            </div>
            <div
              style={{
                marginTop: 10,
                border: '1px solid ' + C.border,
                borderRadius: 6,
                padding: '10px 12px',
                background: '#f8fafc',
              }}
            >
              <div style={{ fontSize: 9.5, fontWeight: 900, color: C.navy, marginBottom: 4 }}>
                À remplir par la comptabilité
              </div>
              {blank('Solvabilité / Limite de crédit')}
              {blank('Conditions de paiement')}
              <div style={{ display: 'flex', gap: 20, marginTop: 4 }}>
                <div style={{ flex: 1 }}>{blank('Date')}</div>
                <div style={{ flex: 1 }}>{blank('Signature')}</div>
              </div>
            </div>
            <div
              style={{
                marginTop: 16,
                borderTop: '1px solid ' + C.border,
                paddingTop: 8,
                textAlign: 'center',
                fontSize: 7.5,
                color: '#777',
              }}
            >
              {soc?.nom || SOC.nom}
              {soc?.adr ? ' — ' + soc.adr : ''}
              {soc?.npa ? ', ' + soc.npa : ''}
              {soc?.email ? ' · ' + soc.email : ''}
              {soc?.reseaux?.site ? ' · ' + soc.reseaux.site : ''}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (selId) {
    const cl = clients.find((c) => c.id === selId);
    if (!cl) return null;
    const cDevis = devis.filter((d) => d.clientId === cl.id);
    const cFact = factures.filter((f) => f.clientId === cl.id);
    const cChant = chantiers.filter((c) => c.clientId === cl.id);
    const caTtc = cFact.filter((f) => f.statut === 'Payée').reduce((s, f) => s + ttcF(f), 0);
    const estMag = estMixte ? univers === 'magasin' : soc?.secteur === 'magasin';
    const tel = (cl.tel || '').replace(/[^0-9]/g, '').replace(/^0/, '41');
    if (edit && form)
      return (
        <Shell
          active="plus"
          onNav={onNav}
          soc={soc}
          back
          onBack={() => setEdit(false)}
          pageTitle="✎ Modifier le client"
          estMixte={estMixte}
          univers={univers}
          setUnivers={setUnivers}
        >
          <Card>
            <div style={{ padding: 14 }}>
              {estMag ? (
                <>
                  <div
                    style={{
                      fontSize: 9.5,
                      fontWeight: 800,
                      color: C.navy,
                      margin: '2px 0 6px',
                      borderBottom: '2px solid ' + C.navyXL,
                      paddingBottom: 3,
                    }}
                  >
                    🏢 Entreprise
                  </div>
                  {[
                    ["Nom de l'entreprise", 'nom'],
                    ['Forme juridique', 'forme'],
                    ['Rue / N°', 'rue'],
                    ['Case postale', 'casePostale'],
                    ['NPA / Lieu', 'npa'],
                    ['Profession / Branche', 'profession'],
                    ['N° TVA', 'tva'],
                    ["Nombre d'employés", 'nbEmployes'],
                  ].map(([lb, k]) => (
                    <Inp
                      key={k}
                      label={lb}
                      value={form[k] || ''}
                      onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))}
                    />
                  ))}
                  <div
                    style={{
                      fontSize: 9.5,
                      fontWeight: 800,
                      color: C.navy,
                      margin: '12px 0 6px',
                      borderBottom: '2px solid ' + C.navyXL,
                      paddingBottom: 3,
                    }}
                  >
                    👤 Contact
                  </div>
                  {[
                    ['Contact principal', 'contactNom'],
                    ['Fonction', 'fonction'],
                    ['Téléphone', 'tel'],
                    ['Téléphone mobile', 'mobile'],
                    ['E-mail', 'email'],
                  ].map(([lb, k]) => (
                    <Inp
                      key={k}
                      label={lb}
                      value={form[k] || ''}
                      onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))}
                    />
                  ))}
                  <div
                    style={{
                      fontSize: 9.5,
                      fontWeight: 800,
                      color: C.navy,
                      margin: '12px 0 6px',
                      borderBottom: '2px solid ' + C.navyXL,
                      paddingBottom: 3,
                    }}
                  >
                    🧾 Facturation
                  </div>
                  {[
                    ['E-mail factures', 'emailFactures'],
                    ['E-mail correspondance', 'emailCorr'],
                    ['Téléphone facturation', 'telFacturation'],
                  ].map(([lb, k]) => (
                    <Inp
                      key={k}
                      label={lb}
                      value={form[k] || ''}
                      onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))}
                    />
                  ))}
                  <div
                    style={{
                      fontSize: 9.5,
                      fontWeight: 800,
                      color: C.purple,
                      margin: '12px 0 6px',
                      borderBottom: '2px solid ' + C.navyXL,
                      paddingBottom: 3,
                    }}
                  >
                    🔐 Conditions (à définir par toi)
                  </div>
                  {[
                    ['Représentant', 'representant'],
                    ['Conditions groupe', 'condGroupe'],
                    ['Limite de crédit', 'limiteCredit'],
                    ['Conditions de paiement', 'condPaiement'],
                  ].map(([lb, k]) => (
                    <Inp
                      key={k}
                      label={lb}
                      value={(form.cond || {})[k] || ''}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, cond: { ...(p.cond || {}), [k]: e.target.value } }))
                      }
                    />
                  ))}
                </>
              ) : (
                [
                  ['Nom', 'nom'],
                  ['Personne de contact', 'contact'],
                  ['Adresse', 'adr'],
                  ['NPA / Ville', 'npa'],
                  ['Téléphone', 'tel'],
                  ['E-mail', 'email'],
                ].map(([lb, k]) => (
                  <Inp
                    key={k}
                    label={lb}
                    value={form[k] || ''}
                    onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))}
                  />
                ))
              )}
              {/* Une société qui ne paie que la facture portant son bon de
                  commande : la facture ne part pas sans lui. */}
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 7,
                  fontSize: 11,
                  color: C.ink,
                  margin: '4px 0 10px',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={exigeBonCommande(form)}
                  onChange={(e) => setForm((p) => avecExigence(p, e.target.checked))}
                />
                🧾 {tr('Ce client exige toujours un n° de bon de commande')}
              </label>
              {/*
                Le delai ne se saisissait qu'a l'ouverture d'un compte : un
                client cree autrement n'en avait jamais, et un delai renegocie
                ne pouvait plus etre corrige.
              */}
              <ChoixDelaiPaiement
                value={form.delaiPaiement ?? ''}
                onChange={(delai) => setForm((p) => ({ ...p, delaiPaiement: delai }))}
              />
              <Btn
                label="✓ Enregistrer les modifications"
                color={C.green}
                full
                disabled={!form.nom?.trim()}
                onClick={() => {
                  // Les remises restent celles du moment : la copie prise à
                  // l'ouverture de la fiche remettait d'anciennes remises.
                  setClients((p) =>
                    p.map((c) =>
                      c.id === cl.id
                        ? { ...c, ...form, remises: c.remises, remiseGlobale: c.remiseGlobale }
                        : c,
                    ),
                  );
                  setEdit(false);
                }}
              />
            </div>
          </Card>
        </Shell>
      );
    return (
      <Shell
        active="plus"
        onNav={onNav}
        soc={soc}
        back
        onBack={() => setSelId(null)}
        pageTitle={cl.nom}
        estMixte={estMixte}
        univers={univers}
        setUnivers={setUnivers}
      >
        <div
          style={{
            background: 'linear-gradient(135deg,' + C.navy + ',' + C.navyM + ')',
            borderRadius: 14,
            padding: 16,
            marginBottom: 12,
          }}
        >
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: 'rgba(255,255,255,.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 20,
                fontWeight: 900,
                color: '#fff',
              }}
            >
              {cl.nom.charAt(0)}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 900, color: '#fff' }}>{cl.nom}</div>
              <div style={{ fontSize: 9, color: 'rgba(255,255,255,.55)' }}>
                {cl.adr} · {cl.npa}
              </div>
            </div>
          </div>
          {/* Les chantiers d'un client sont du bâtiment ; le magasin compte ses offres. */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: estMag ? '1fr 1fr' : '1fr 1fr 1fr',
              gap: 6,
            }}
          >
            {[
              [termesOffre(soc, { estMag }).Pluriel, cDevis.length],
              [trU('Factures'), cFact.length],
              ...(estMag ? [] : [[trU('Chantiers'), cChant.length]]),
            ].map(([l, v]) => (
              <div
                key={l}
                style={{
                  background: 'rgba(255,255,255,.1)',
                  borderRadius: 8,
                  padding: '7px 4px',
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: 6.5, color: 'rgba(255,255,255,.55)' }}>{l}</div>
                <div style={{ fontSize: 12, fontWeight: 900, color: '#fff' }}>{v}</div>
              </div>
            ))}
          </div>
        </div>
        <div
          style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}
        >
          {cl.tel && (
            <a href={'tel:' + cl.tel} style={{ textDecoration: 'none' }}>
              <div
                style={{
                  background: C.navy,
                  color: '#fff',
                  borderRadius: 9,
                  padding: '10px',
                  textAlign: 'center',
                  fontSize: 10,
                  fontWeight: 700,
                }}
              >
                📞 Appeler
              </div>
            </a>
          )}
          {cl.tel && (
            <a
              href={'https://wa.me/' + tel}
              target="_blank"
              rel="noreferrer"
              style={{ textDecoration: 'none' }}
            >
              <div
                style={{
                  background: C.green,
                  color: '#fff',
                  borderRadius: 9,
                  padding: '10px',
                  textAlign: 'center',
                  fontSize: 10,
                  fontWeight: 700,
                }}
              >
                WhatsApp
              </div>
            </a>
          )}
          {cl.email && (
            <a href={'mailto:' + cl.email} style={{ textDecoration: 'none' }}>
              <div
                style={{
                  background: C.teal,
                  color: '#fff',
                  borderRadius: 9,
                  padding: '10px',
                  textAlign: 'center',
                  fontSize: 10,
                  fontWeight: 700,
                }}
              >
                ✉ Email
              </div>
            </a>
          )}
        </div>
        <a
          href={
            'data:text/vcard;charset=utf-8,' +
            encodeURIComponent(
              'BEGIN:VCARD\nVERSION:3.0\nFN:' +
                (cl.nom || '') +
                '\nN:' +
                (cl.nom || '') +
                ';;;\nORG:' +
                (cl.nom || '') +
                '\n' +
                (cl.tel ? 'TEL;TYPE=CELL:' + cl.tel + '\n' : '') +
                (cl.email ? 'EMAIL;TYPE=INTERNET:' + cl.email + '\n' : '') +
                (cl.adr || cl.npa
                  ? 'ADR;TYPE=HOME:;;' + (cl.adr || '') + ';' + (cl.npa || '') + ';;;\n'
                  : '') +
                'END:VCARD',
            )
          }
          download={(cl.nom || 'contact').replace(/[^a-zA-Z0-9]/g, '_') + '.vcf'}
          style={{ textDecoration: 'none' }}
        >
          <div
            style={{
              background: C.purple,
              color: '#fff',
              borderRadius: 9,
              padding: '11px',
              textAlign: 'center',
              fontSize: 10.5,
              fontWeight: 700,
              marginBottom: 12,
            }}
          >
            📇 Ajouter à mes contacts (téléphone)
          </div>
        </a>
        {/* Le chiffre d'affaires du client ne regarde que la direction : la
            secrétaire et le technicien ne voient que les remises et les points. */}
        {(voitLeBenefice() || estMag) && (
          <Card style={{ marginBottom: 11 }}>
            <Bar
              icon="💰"
              title={voitLeBenefice() ? "Chiffre d'affaires" : 'Remises et fidélité'}
              color={C.green}
            />
            <div style={{ padding: 13 }}>
              {voitLeBenefice() && (
                <FR
                  label="Encaissé (factures payées)"
                  value={DEVISE + ' ' + fmt(caTtc)}
                  color={C.green}
                  last={!estMag}
                />
              )}

              {estMag && (
                <FR
                  label="Remises par article"
                  value={Object.keys(cl.remises || {}).length + ' article(s)'}
                />
              )}
              {/* Un client pas encore enregistré n'a pas de numéro : l'envoi
                  attend qu'il le soit (quelques secondes). */}
              {estMag && Number.isInteger(cl.id) && (
                <div style={{ margin: '4px 0 10px' }}>
                  <Btn
                    label="Envoyer le catalogue à ce client"
                    color={C.teal}
                    full
                    onClick={() => setEnvoiCatalogue(true)}
                  />
                  <div style={{ fontSize: 9, color: C.muted, marginTop: 4 }}>
                    {tr('Prix public + ses prix')} ·{' '}
                    {tr('ses remises par article, celles de sa fiche client')}
                  </div>
                </div>
              )}
              {/*
              Les points s'accumulent a la caisse et n'apparaissaient que dans
              le selecteur de client d'une vente. Sa fiche — l'endroit ou on
              les cherche quand le client les reclame — n'en disait rien.
            */}
              {estMag && (
                <FR
                  label="⭐ Points de fidélité"
                  value={(cl.points || 0) + ' point' + ((cl.points || 0) > 1 ? 's' : '')}
                  color={(cl.points || 0) > 0 ? C.amber : undefined}
                  last
                />
              )}
            </div>
          </Card>
        )}
        <Card style={{ marginBottom: 11 }}>
          <Bar
            icon="👥"
            title="Personnes à contacter"
            color={C.teal}
            sub={
              estMag
                ? 'Directeur, techniciens, collaborateurs : ils reçoivent le catalogue'
                : 'Directeur, techniciens, collaborateurs : à ajouter en un toucher à l’envoi de l’offre'
            }
          />
          <PersonnesAContacter client={cl} setClients={setClients} />
        </Card>
        {/* La liste du client : la marchandise, la sous-traitance et les photos
            de toutes ses offres et factures, même payées. */}
        {(cDevis.length > 0 || cFact.length > 0) && (
          <ListeDuClientEnCarte devis={cDevis} factures={cFact} chantiers={cChant} />
        )}
        {cDevis.length > 0 && (
          <Card style={{ marginBottom: 11 }}>
            <Bar icon="📝" title={termesOffre(soc, { estMag: estMagC }).Pluriel} color={C.navy} />
            <div style={{ padding: '4px 13px 10px' }}>
              {cDevis.map((d) => (
                <div
                  key={d.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '8px 0',
                    borderBottom: '1px solid ' + C.border,
                    fontSize: 10,
                  }}
                >
                  <span>
                    {numeroDe(d) || d.id} · {d.date}
                  </span>
                  <Pill
                    label={d.statut}
                    color={
                      d.statut === 'Accepté' ? C.green : d.statut === 'Refusé' ? C.red : C.amber
                    }
                    size={7}
                  />
                </div>
              ))}
            </div>
          </Card>
        )}
        {cFact.length > 0 && (
          <Card style={{ marginBottom: 11 }}>
            <Bar icon="🧾" title="Factures" color={C.teal} />
            <div style={{ padding: '4px 13px 10px' }}>
              {cFact.map((f) => (
                <div
                  key={f.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '8px 0',
                    borderBottom: '1px solid ' + C.border,
                    fontSize: 10,
                  }}
                >
                  <span>
                    {numeroDe(f) || f.id} · {DEVISE} {fmt(ttcF(f))}
                  </span>
                  <Pill
                    label={f.statut}
                    color={f.statut === 'Payée' ? C.green : C.amber}
                    size={7}
                  />
                </div>
              ))}
            </div>
          </Card>
        )}
        {/* L'essentiel en vue ; le suivi commercial, le compte client et les
            données personnelles sous « ⋯ Plus ». Au magasin, tout reste
            ouvert comme dans l'ancienne version : le compte client (numéro,
            validation, fiche d'ouverture A4) se cherchait derrière le bouton. */}
        {!estMag && (
          <button
            type="button"
            aria-expanded={plusFiche}
            onClick={() => setPlusFiche((o) => !o)}
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              gap: 6,
              padding: '10px 0',
              marginBottom: 11,
              borderRadius: 11,
              border: '1px solid ' + C.border,
              background: C.white,
              color: C.navy,
              fontSize: 11.5,
              fontWeight: 800,
              cursor: 'pointer',
            }}
          >
            <span aria-hidden="true">⋯</span>
            <span>{tr('Plus')}</span>
            <span aria-hidden="true">{plusFiche ? '▴' : '▾'}</span>
          </button>
        )}
        {(plusFiche || estMag) && (
          <>
            <Card style={{ marginBottom: 11 }}>
              <Bar
                icon="🎯"
                title="Suivi commercial (CRM)"
                color={C.amber}
                sub="Statut, relance, notes"
              />
              <div style={{ padding: 13 }}>
                <div
                  style={{
                    fontSize: 8.5,
                    fontWeight: 800,
                    color: C.muted,
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                    marginBottom: 5,
                  }}
                >
                  Statut
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 11 }}>
                  {['Prospect', 'En cours', 'Client', 'Perdu'].map((stx) => {
                    const on = (cl.crmStatut || '') === stx;
                    const col =
                      stx === 'Client'
                        ? C.green
                        : stx === 'Perdu'
                          ? C.red
                          : stx === 'En cours'
                            ? C.amber
                            : C.teal;
                    return (
                      <span
                        key={stx}
                        onClick={() =>
                          setClients((p) =>
                            p.map((c) => (c.id === cl.id ? { ...c, crmStatut: stx } : c)),
                          )
                        }
                        style={{
                          fontSize: 9.5,
                          fontWeight: 700,
                          padding: '6px 11px',
                          borderRadius: 99,
                          cursor: 'pointer',
                          background: on ? col : C.white,
                          color: on ? '#fff' : C.grey,
                          border: '1px solid ' + (on ? col : C.border),
                        }}
                      >
                        {stx}
                      </span>
                    );
                  })}
                </div>
                <Inp
                  label="Date de relance"
                  value={cl.relanceLe || ''}
                  onChange={(e) =>
                    setClients((p) =>
                      p.map((c) => (c.id === cl.id ? { ...c, relanceLe: e.target.value } : c)),
                    )
                  }
                  placeholder="JJ.MM.AAAA"
                />
                <HistoriqueEchanges client={cl} setClients={setClients} />
              </div>
            </Card>
            {estMag && (
              <Card style={{ marginBottom: 11 }}>
                <Bar
                  icon="🪪"
                  title="Compte client"
                  color={C.purple}
                  sub={
                    cl.compteStatut === 'demande'
                      ? 'Demande en attente de validation'
                      : 'Numéro permanent'
                  }
                />
                <div style={{ padding: 13 }}>
                  <FR label="N° client (permanent)" value={String(cl.id)} />
                  {cl.ide && <FR label="N° IDE / Registre du commerce" value={cl.ide} />}
                  <FR
                    label="Statut"
                    value={cl.compteStatut === 'demande' ? 'Demande en attente' : 'Compte ouvert'}
                    color={cl.compteStatut === 'demande' ? C.amber : C.green}
                  />
                  {(cl.docRC || cl.docPoursuite) && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 9 }}>
                      {cl.docRC && (
                        <a
                          href={cl.docRC}
                          target="_blank"
                          rel="noreferrer"
                          download={cl.docRCNom || 'registre.pdf'}
                          style={{ flex: 1, textDecoration: 'none' }}
                        >
                          <div
                            style={{
                              background: C.navyXL,
                              border: '1px solid ' + C.border,
                              borderRadius: 8,
                              padding: '9px',
                              textAlign: 'center',
                              fontSize: 8.5,
                              fontWeight: 700,
                              color: C.navy,
                            }}
                          >
                            📄 Registre du commerce
                          </div>
                        </a>
                      )}
                      {cl.docPoursuite && (
                        <a
                          href={cl.docPoursuite}
                          target="_blank"
                          rel="noreferrer"
                          download={cl.docPoursuiteNom || 'poursuites.pdf'}
                          style={{ flex: 1, textDecoration: 'none' }}
                        >
                          <div
                            style={{
                              background: C.navyXL,
                              border: '1px solid ' + C.border,
                              borderRadius: 8,
                              padding: '9px',
                              textAlign: 'center',
                              fontSize: 8.5,
                              fontWeight: 700,
                              color: C.navy,
                            }}
                          >
                            📄 Extrait de poursuites
                          </div>
                        </a>
                      )}
                    </div>
                  )}
                  {cl.compteStatut === 'demande' && (
                    <Btn
                      label="✓ Valider et ouvrir le compte"
                      color={C.green}
                      full
                      onClick={() =>
                        setClients((p) =>
                          p.map((c) =>
                            c.id === cl.id
                              ? { ...c, compteStatut: 'actif', dateOuverture: today() }
                              : c,
                          ),
                        )
                      }
                      style={{ marginTop: 10 }}
                    />
                  )}
                  <Btn
                    label="📄 Fiche d'ouverture (A4 imprimable)"
                    color={C.navy}
                    full
                    onClick={() => setFicheA4(cl.id)}
                    style={{ marginTop: 8 }}
                  />
                  {/* Le dossier du client : la fiche d'ouverture qu'il renvoie
                      signée (photo ou PDF), ce qu'il a signé ou envoyé. Il se
                      retrouve dans Compta → Documents → Comptes clients. */}
                  <DocumentsJoints
                    genre="CLIENT"
                    documentId={cl.id}
                    sortes={SORTES_CLIENT}
                    titre={tr('📎 DOCUMENTS DU CLIENT')}
                  />
                </div>
              </Card>
            )}
            {/* Le serveur réserve l'état et l'effacement à la direction. */}
            {ACCESS_ROLE === 'patron' && (
              <DonneesPersonnellesFiche sujet="client" id={cl.id} nom={cl.nom} />
            )}
          </>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
          <Btn
            label="✎ Modifier"
            color={C.navy}
            full
            onClick={() => {
              setForm({ ...cl });
              setEdit(true);
            }}
          />
          <Btn
            label="🗑 Supprimer"
            ghost
            danger
            color={C.red}
            full
            onClick={() => supprimeClient(cl)}
          />
        </div>
        {envoiCatalogue && (
          <EnvoiCatalogue
            client={cl}
            ttc={false}
            setClients={setClients}
            retour="← Retour"
            onFermer={() => setEnvoiCatalogue(false)}
          />
        )}
      </Shell>
    );
  }

  const ql = q.trim().toLowerCase();
  const liste = ordreDeSaisie(clients).filter(
    (c) => !ql || c.nom.toLowerCase().includes(ql) || (c.npa || '').toLowerCase().includes(ql),
  );
  return (
    <Shell
      active="plus"
      onNav={onNav}
      soc={soc}
      pageTitle="👥 Clients"
      estMixte={estMixte}
      univers={univers}
      setUnivers={setUnivers}
    >
      {!add ? (
        // Un vrai bouton : au clavier comme au lecteur d'ecran, le bloc
        // cliquable d'avant n'existait pas.
        <button
          type="button"
          onClick={() => {
            setForm({ nom: '', adr: '', npa: '', tel: '', email: '' });
            setAdd(true);
          }}
          style={{
            background: C.navy,
            border: 'none',
            borderRadius: 12,
            padding: 16,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            marginBottom: 11,
            width: '100%',
            fontFamily: 'inherit',
          }}
        >
          <span style={{ fontSize: 22, color: '#fff' }}>＋</span>
          <span style={{ fontSize: 14, fontWeight: 800, color: '#fff' }}>Nouveau client</span>
        </button>
      ) : (
        <Card style={{ marginBottom: 11, border: '2px solid ' + C.green }}>
          <Bar icon="👤" title="Nouveau client" color={C.green} />
          <div style={{ padding: 13 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
              <BoutonContacts
                sm
                full
                color={C.navy}
                onContact={recoitContact}
                onEchec={setCliMsg}
              />
              <div style={{ flex: 1 }}>
                <Btn
                  label="✍ Saisir à la main"
                  sm
                  ghost
                  color={C.navy}
                  full
                  onClick={() => setCliMsg('')}
                />
              </div>
            </div>
            <div style={{ fontSize: 8, color: C.muted, marginBottom: 8, lineHeight: 1.4 }}>
              « Depuis mes contacts » ouvre le carnet du téléphone et remplit la fiche. Sinon,
              saisis à la main.
            </div>
            {cliMsg && (
              <div
                style={{
                  background: C.navyXL,
                  border: '1px solid ' + C.border,
                  borderRadius: 8,
                  padding: '8px 10px',
                  fontSize: 9,
                  color: C.grey,
                  marginBottom: 8,
                  lineHeight: 1.4,
                }}
              >
                {cliMsg}
              </div>
            )}
            {[
              ['Nom', 'nom'],
              ['Personne de contact', 'contact'],
              ['Adresse', 'adr'],
              ['NPA / Ville', 'npa'],
              ['Téléphone', 'tel'],
              ['E-mail', 'email'],
            ].map(([lb, k]) => (
              <Inp
                key={k}
                label={lb}
                value={form?.[k] || ''}
                onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))}
              />
            ))}
            <div style={{ display: 'flex', gap: 6 }}>
              <div style={{ flex: 1 }}>
                <Btn
                  label="✓ Créer"
                  sm
                  color={C.green}
                  full
                  disabled={!form?.nom?.trim()}
                  onClick={() => {
                    const id = idProvisoire();
                    setClients((p) => [...p, { id, ...form, remiseGlobale: 0, remises: {} }]);
                    setAdd(false);
                    setCliMsg('');
                  }}
                />
              </div>
              <Btn
                label="Annuler"
                sm
                ghost
                color={C.grey}
                onClick={() => {
                  setAdd(false);
                  setCliMsg('');
                }}
              />
            </div>
          </div>
        </Card>
      )}
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="🔎 Rechercher un client"
        style={inpFS}
      />
      {liste.map((c) => {
        const nF = factures.filter((f) => f.clientId === c.id).length;
        const nD = devis.filter((d) => d.clientId === c.id).length;
        return (
          <Card
            key={c.id}
            style={{ marginBottom: 8, cursor: 'pointer' }}
            onClick={() => {
              setSelId(c.id);
              setEdit(false);
            }}
          >
            <div
              style={{
                padding: '12px 14px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div style={{ display: 'flex', gap: 11, alignItems: 'center' }}>
                <div
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 10,
                    background: C.navyXL,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 900,
                    color: C.navy,
                  }}
                >
                  {c.nom.charAt(0)}
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700 }}>{c.nom}</div>
                  <div style={{ fontSize: 8.5, color: C.muted }}>
                    {c.npa}
                    {c.remiseGlobale > 0 ? ' · remise ' + c.remiseGlobale + '%' : ''}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 8, color: C.muted }}>
                    {nD} devis · {nF} fact.
                  </div>
                </div>
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelId(c.id);
                    setForm({ ...c });
                    setEdit(true);
                  }}
                  style={{ fontSize: 15, cursor: 'pointer', padding: '0 2px' }}
                >
                  ✏️
                </span>
                {/* Supprimer d'ici : le bouton n'existait qu'au bas de la fiche. */}
                <button
                  type="button"
                  aria-label={trf('Supprimer {0}', c.nom)}
                  onClick={(e) => {
                    e.stopPropagation();
                    supprimeClient(c);
                  }}
                  style={{
                    fontSize: 15,
                    cursor: 'pointer',
                    padding: '0 2px',
                    border: 'none',
                    background: 'none',
                  }}
                >
                  🗑
                </button>
                <span style={{ color: C.muted }}>›</span>
              </div>
            </div>
          </Card>
        );
      })}
      {liste.length === 0 && (
        <Card>
          <div style={{ padding: 20, textAlign: 'center', color: C.muted, fontSize: 11 }}>
            Aucun client
          </div>
        </Card>
      )}
    </Shell>
  );
}

/** La liste du client dans sa carte, absente tant qu'elle est vide. */
function ListeDuClientEnCarte({ devis, factures, chantiers }) {
  return (
    <ListeDuClient
      devis={devis}
      factures={factures}
      chantiers={chantiers}
      enveloppe={(contenu) => (
        <Card style={{ marginBottom: 11 }}>
          <Bar
            icon="📦"
            title="Marchandise, sous-traitance et photos"
            color={C.purple}
            sub="Ses offres, ses factures et ses chantiers"
          />
          <div style={{ padding: '4px 13px 10px' }}>{contenu}</div>
        </Card>
      )}
    />
  );
}
